import { useEffect, useRef, useState } from 'react';
import { FlowingWaves, MandalaOrnament } from '@/components/feature/DrawingLines';

const stats = [
  { value: 97, suffix: '%', label: '가맹점 만족도', roman: 'I' },
  { value: 120, suffix: '+', label: '전국 가맹점', roman: 'II' },
  { value: 8, suffix: '년', label: '브랜드 운영 경력', roman: 'III' },
  { value: 50, suffix: 'K+', label: '누적 시술 건수', roman: 'IV' },
];

function CountUp({ target, suffix, active }: { target: number; suffix: string; active: boolean }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!active) return;
    let current = 0;
    const duration = 1800;
    const steps = 60;
    const increment = target / steps;
    const interval = duration / steps;

    const timer = setInterval(() => {
      current += increment;
      if (current >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, interval);

    return () => clearInterval(timer);
  }, [active, target]);

  return (
    <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: '2px' }}>
      <span
        style={{
          fontFamily: "'Montserrat', sans-serif",
          fontWeight: 700,
          letterSpacing: '-0.02em',
          color: '#FFFFFF',
          fontSize: 'inherit',
        }}
      >
        {count}
      </span>
      <span
        style={{
          fontFamily: "'Montserrat', sans-serif",
          fontWeight: 400,
          fontSize: '0.5em',
          color: '#AAAAAA',
          marginLeft: '2px',
        }}
      >
        {suffix}
      </span>
    </span>
  );
}

export default function StatsSection() {
  const ref = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActive(true);
            observer.disconnect();
          }
        });
      },
      { threshold: 0, rootMargin: '0px 0px -50px 0px' }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  return (
    <section
      ref={ref}
      style={{ background: '#1A1A1A', padding: '64px 0', overflow: 'hidden', position: 'relative' }}
    >
      {/* Flowing wave lines background */}
      <FlowingWaves color="rgba(201,169,110,0.08)" lineCount={5} />

      <div style={{ maxWidth: '1440px', margin: '0 auto', padding: '0 20px', position: 'relative', zIndex: 1 }}>
        {/* Label + Mandala */}
        <div style={{ textAlign: 'center', marginBottom: '40px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
          <MandalaOrnament size={60} color="rgba(201,169,110,0.25)" delay={0.2} />
          <p
            style={{
              fontFamily: "'Montserrat', sans-serif",
              fontSize: '10px',
              letterSpacing: '0.4em',
              textTransform: 'uppercase',
              color: '#888888',
              margin: 0,
            }}
          >
            Numbers Speak
          </p>
        </div>

        {/* Stats Grid — 모바일 2열, 데스크탑 4열 */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-0">
          {stats.map((s, i) => (
            <div
              key={s.label}
              className={[
                'flex flex-col items-center text-center py-8 md:py-0',
                // 모바일: 우측 보더 (짝수 인덱스만), 하단 보더 (첫 두 개)
                i % 2 === 0 ? 'border-r border-white/8' : '',
                i < 2 ? 'border-b border-white/8 md:border-b-0' : '',
                // 데스크탑: 마지막 제외 우측 보더
                i < stats.length - 1 ? 'md:border-r md:border-white/8' : '',
              ].join(' ')}
              style={{ padding: '32px 16px' }}
            >
              {/* Gold top ornament */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '16px', gap: '5px' }}>
                <div style={{ width: '1px', height: '16px', background: 'linear-gradient(to bottom, transparent, #C9A96E)' }} />
                <span
                  style={{
                    fontFamily: "'Cinzel', serif",
                    fontSize: '9px',
                    fontWeight: 400,
                    letterSpacing: '0.35em',
                    color: '#C9A96E',
                    lineHeight: 1,
                  }}
                >
                  {s.roman}
                </span>
                <div style={{ width: '3px', height: '3px', borderRadius: '50%', background: '#C9A96E', opacity: 0.7 }} />
              </div>

              {/* Number */}
              <div
                className="text-5xl md:text-5xl"
                style={{
                  lineHeight: 1,
                  marginBottom: '14px',
                  color: '#FFFFFF',
                }}
              >
                <CountUp target={s.value} suffix={s.suffix} active={active} />
              </div>

              {/* Gold divider */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', marginBottom: '10px' }}>
                <div style={{ width: '14px', height: '1px', background: 'rgba(201,169,110,0.3)' }} />
                <div style={{ width: '3px', height: '3px', borderRadius: '50%', border: '1px solid rgba(201,169,110,0.5)', flexShrink: 0 }} />
                <div style={{ width: '14px', height: '1px', background: 'rgba(201,169,110,0.3)' }} />
              </div>

              {/* Label */}
              <p
                style={{
                  fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif",
                  fontSize: '11px',
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  color: '#CCCCCC',
                  margin: 0,
                  fontWeight: 500,
                  wordBreak: 'keep-all',
                }}
              >
                {s.label}
              </p>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        .border-white\\/8 { border-color: rgba(255,255,255,0.08); }
      `}</style>
    </section>
  );
}
